import Link from 'next/link';
import { MouseEventHandler } from 'react';
import { getCategoryUrl } from 'src/modules/Categories';
import useLocalization from 'src/services/localization/useLocalization';
import { Product } from '../../../../redux/apiTypes';
import Truncate from '../../Truncate/Truncate';
import { styles } from './BigProductCardDescription.styles';

interface Props {
  product: Product
  language: string
  taxonsAsText?: boolean
  onNewRouteLinkClick: MouseEventHandler<HTMLAnchorElement>
}

const BigProductCardDescription = (props: Props) => {
  const { product, taxonsAsText, onNewRouteLinkClick, language } = props;
  const { description, nutrition_facts, ingredients, taxons, category_id: categoryId, category_filters } = product;
  const maxDescriptionLength = 170;

  const hasPrTaxon = !!taxons.find((t) => t.type === 'pr');
  const hasImporterTaxon = !!taxons.find((t) => t.type === 'importer');

  const hasTaxons = !!taxons.length;
  const hasNutritionFacts = !!Object.keys(nutrition_facts).length;
  const hasIngredients = !!ingredients.length;
  const localize = useLocalization();

  if (!description && !hasTaxons && !hasNutritionFacts && !hasIngredients) {
    return null;
  }

  return (
    <div data-marker='Product About'>
      <h2>{localize('products.about-title')}</h2>

      <div className='BigProductCardDescription__content'>
        {!!description && (
          <div className='BigProductCardDescription__section'>
            <h4 className='BigProductCardDescription__heading'>
              {localize('products.description')}
            </h4>

            <div className='BigProductCardDescription__text'>
              <Truncate
                text={description}
                maxLength={maxDescriptionLength}
              />
            </div>
          </div>
        )}

        {hasNutritionFacts && (
          <div className='BigProductCardDescription__section'>
            <h4 className='BigProductCardDescription__heading'>
              {localize('products.nutrition-facts.title')}
            </h4>

            <ul className='BigProductCardDescription__factsList'>
              { Object.entries(nutrition_facts).map( ([key, value]) => {
                return (
                  <li
                    key={key}
                    className='BigProductCardDescription__infoEntry'
                  >
                    <span className='BigProductCardDescription__entryTitle'>
                        {localize(`product_facts_${key}`)}
                    </span>
                    <span className='BigProductCardDescription__entryValue'>
                      {value}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {hasIngredients && (
          <div className='BigProductCardDescription__section'>
            <h4 className='BigProductCardDescription__heading'>
              {localize('products.ingredients')}
            </h4>

            <div className='BigProductCardDescription__text'>
              <Truncate
                text={ingredients.join(', ')}
                maxLength={maxDescriptionLength}
              />
            </div>
          </div>
        )}

        {hasTaxons && (
          <div className='BigProductCardDescription__section'>
            <h4 className='BigProductCardDescription__heading'>
              {localize('products.general-info')}
            </h4>

            <ul className='BigProductCardDescription__factsList'>
              {taxons.map((taxon) => {
                if (!taxon) {
                  return null;
                }

                // if there are both, show only taxon "pr"
                if (hasPrTaxon && hasImporterTaxon && taxon.type === 'importer') {
                  return null;
                }

                const title = taxon.type === 'country'
                  ? `${taxon.name[0].toUpperCase()}${taxon.name.slice(1)}`
                  : taxon.name;

                const asLink = !taxonsAsText &&
                  category_filters.includes(taxon.type) &&
                  categoryId;
                const baseCategoryUrl = getCategoryUrl({
                  language,
                  categoryId,
                });
                const taxonFilterSegment = `${taxon.type}=${taxon.value}/`;

                return (
                  <li
                    key={taxon.type}
                    className='BigProductCardDescription__infoEntry'
                    data-marker={`Taxon ${taxon.type}`}
                  >
                    <span className='BigProductCardDescription__entryTitle'>
                      {taxon.label}
                    </span>

                    <span className='BigProductCardDescription__entryValue'>
                      {asLink
                        ? (
                          <Link
                            href="/[lang]/categories/[category-id]/[firstLevelFilter]"
                            as={`${baseCategoryUrl}${taxonFilterSegment}`}
                            onClick={onNewRouteLinkClick}
                          >
                            <span className="BigProductCardDescription__link">
                              {title}
                            </span>
                          </Link>
                        )
                        : title
                      }
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        </div>

        <style jsx>{styles}</style>
    </div>
  );
};


export default BigProductCardDescription;
